import { ChartColumn, ChefHat, ChevronDown, Ellipsis, House, LogOut, Megaphone, Menu, ScanLine, Settings, Sparkles, TabletSmartphone, TicketPercent, Users, type LucideIcon } from 'lucide-react';
import { createContext, useContext, useEffect, useState, type MouseEvent, type ReactNode } from 'react';
import { signOut, type SignedIn } from './api.ts';
import { FaceMark, ProductMark, type Face } from './brand.tsx';
import { navigate, useAddress } from './router.ts';
import { LanguageMenu } from './language.tsx';
import { useFrame, type ScreenKind } from './scope.ts';
import type { NavEntry, NavIcon } from './routes.tsx';
import { strings } from './strings.ts';
import { UiAvatar } from './ui/avatar.tsx';
import { UiBadge } from './ui/badge.tsx';
import { UiButton } from './ui/button.tsx';
import { cn } from './ui/cn.ts';
import { UiMenu, UiMenuContent, UiMenuItem, UiMenuLabel, UiMenuSeparator, UiMenuTrigger } from './ui/dropdown-menu.tsx';
import { UiSheet, UiSheetContent } from './ui/sheet.tsx';

/**
 * The frame of every staff page, laid out as a CRM is: the sidebar of
 * modules on the left (a drawer behind a menu button under 1024 px, a tab
 * bar of the built ones on a phone), the top bar (ask or search, the
 * brand, who is signed in), the page, and the dark status bar at the foot.
 *
 * The built modules are the features' staff pages (their NavDecl, composed
 * by the shell into NavProvider); the rest are the shell's own list of what
 * is not built yet, drawn muted with "Soon" and not reachable.
 */

const ICONS = {
  home: House,
  people: Users,
  scan: ScanLine,
  megaphone: Megaphone,
  ticket: TicketPercent,
  kiosk: TabletSmartphone,
  kitchen: ChefHat,
  chart: ChartColumn,
  settings: Settings,
} as const satisfies Record<NavIcon, LucideIcon>;

/** The modules not built yet, by the rank a feature's NavDecl sits against. Settings is pinned at the foot. */
const SOON: readonly {
  readonly label: string;
  readonly icon: NavIcon;
  readonly rank: number;
}[] = [
  { label: strings.shell.home, icon: 'home', rank: 0 },
  { label: strings.shell.kiosk, icon: 'kiosk', rank: 50 },
  { label: strings.shell.kitchen, icon: 'kitchen', rank: 60 },
  { label: strings.shell.reports, icon: 'chart', rank: 70 },
];
const PINNED = { label: strings.shell.settings, icon: 'settings' } as const;

const NavContext = createContext<readonly NavEntry[]>([]);

/** The shell hands the features' composed menu entries to every page under it. */
export function NavProvider({ entries, children }: { readonly entries: readonly NavEntry[]; readonly children: ReactNode }) {
  return <NavContext.Provider value={entries}>{children}</NavContext.Provider>;
}

/** What a page under StaffLayout tells the frame it is drawn in: who and where, and its kind of screen. */
export interface FramedPage {
  readonly staff: StaffContext;
  readonly kind: ScreenKind;
}

/** Under StaffLayout, how a page tells the frame what it is (Screen does); null outside one, where a Screen draws its own frame. */
export const FrameSlot = createContext<((page: FramedPage) => void) | null>(null);

/** The page area of the staff frame: a reading column, or the frame's full width for a screen of records. */
export const pageArea = (wide: boolean) => `flex w-full flex-col gap-s5 px-s4 pt-s5 pb-s7 tablet:px-s6 tablet:pt-s6 ${wide ? 'max-w-[96rem]' : 'max-w-[52rem]'}`;

/** Who and where: the page's group, the signed-in staff (null while read), the group's face, and what a tap on the current entry does (a page back to its start). */
export interface StaffContext {
  readonly groupId: string;
  readonly who: SignedIn | null;
  readonly group: Face | null;
  /** The outlet this device works at, when the page knows one (a station's, or the group's only outlet). */
  readonly outlet?: string | null;
  readonly onCurrent?: () => void;
}

type Item =
  | {
      readonly kind: 'built';
      readonly label: string;
      readonly icon: NavIcon;
      readonly rank: number;
      readonly href: string;
      readonly current: boolean;
      readonly count: string | null;
    }
  | {
      readonly kind: 'soon';
      readonly label: string;
      readonly icon: NavIcon;
      readonly rank: number;
    };

/** The entries' figures, by group and path: read once a session, so moving between pages does not read them again. */
const counted = new Map<string, string | null>();

function useCounts(entries: readonly NavEntry[], groupId: string, signedIn: boolean): ReadonlyMap<string, string | null> {
  const [, setRead] = useState(0);
  useEffect(() => {
    if (!signedIn) return;
    for (const entry of entries) {
      const key = `${groupId} ${entry.path}`;
      if (entry.count === undefined || counted.has(key)) continue;
      counted.set(key, null);
      void entry.count(groupId).then(
        (count) => {
          counted.set(key, count);
          setRead((n) => n + 1);
        },
        () => undefined,
      );
    }
  }, [entries, groupId, signedIn]);
  return new Map(entries.map((entry) => [entry.path, counted.get(`${groupId} ${entry.path}`) ?? null]));
}

function useItems(staff: StaffContext): readonly Item[] {
  const entries = useContext(NavContext);
  const counts = useCounts(entries, staff.groupId, staff.who !== null);
  const { path } = useAddress();
  const built = entries
    .filter((entry) => entry.shows === undefined || (staff.who !== null && entry.shows(staff.who)))
    .map((entry) => {
      const href = entry.path;
      return {
        kind: 'built' as const,
        label: entry.label,
        icon: entry.icon,
        rank: entry.rank,
        href,
        current: path.replace(/\/$/, '').toLowerCase() === href.toLowerCase(),
        count: counts.get(entry.path) ?? null,
      };
    });
  return [...built, ...SOON.map((soon) => ({ kind: 'soon' as const, ...soon }))].sort((a, b) => a.rank - b.rank);
}

/** Moves in place on a plain click (a new tab with a modifier key); the current entry runs `onCurrent` instead. */
function follow(event: MouseEvent<HTMLAnchorElement>, href: string, current: boolean, onCurrent: (() => void) | undefined, after?: () => void) {
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
  event.preventDefault();
  if (current && onCurrent !== undefined) onCurrent();
  else if (!current) navigate(href);
  after?.();
}

const ITEM = 'relative flex min-h-control items-center gap-s3 rounded-control px-s3 text-body font-medium [&_svg]:size-icon [&_svg]:shrink-0';

function NavLine({ item, onCurrent, after }: { readonly item: Item; readonly onCurrent: (() => void) | undefined; readonly after?: () => void }) {
  const Icon = ICONS[item.icon];
  if (item.kind === 'soon')
    return (
      <li>
        <span aria-disabled="true" className={cn(ITEM, 'cursor-default text-muted')}>
          <Icon aria-hidden="true" className="opacity-70" />
          <span className="flex-1">{item.label}</span>
          <UiBadge tone="soon">{strings.shell.soon}</UiBadge>
        </span>
      </li>
    );
  return (
    <li>
      <a
        href={item.href}
        aria-current={item.current ? 'page' : undefined}
        className={cn(
          ITEM,
          'text-ink-2 hover:bg-sunk hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-brand',
          'aria-[current=page]:bg-brand-soft aria-[current=page]:font-semibold aria-[current=page]:text-brand aria-[current=page]:before:absolute aria-[current=page]:before:inset-y-s2 aria-[current=page]:before:-left-s3 aria-[current=page]:before:w-s1 aria-[current=page]:before:rounded-r-chip aria-[current=page]:before:bg-brand-field',
        )}
        onClick={(event) => {
          follow(event, item.href, item.current, onCurrent, after);
        }}
      >
        <Icon aria-hidden="true" />
        <span className="flex-1">{item.label}</span>
        {item.count !== null && <span className="text-caption font-medium text-muted tabular-nums">{item.count}</span>}
      </a>
    </li>
  );
}

/** The modules, then Settings at the foot. */
function NavList({ staff, after }: { readonly staff: StaffContext; readonly after?: () => void }) {
  const items = useItems(staff);
  return (
    <nav data-density="calm" aria-label={strings.shell.menu} className="flex min-h-0 flex-1 flex-col overflow-y-auto px-s3 py-s4">
      <ul className="flex flex-col gap-s1">
        {items.map((item) => (
          <NavLine key={item.label} item={item} onCurrent={staff.onCurrent} {...(after !== undefined && { after })} />
        ))}
      </ul>
      <ul className="mt-auto flex flex-col gap-s1 border-t border-line pt-s3">
        <NavLine item={{ kind: 'soon', rank: Infinity, ...PINNED }} onCurrent={undefined} />
      </ul>
    </nav>
  );
}

/** Whose command center it is: the group's wordmark and the outlet, the height of the top bar so their lines meet. The product's mark and name until the group is read. */
function SidebarHead({ staff }: { readonly staff: StaffContext }) {
  return (
    <div className="flex h-topbar shrink-0 items-center gap-s3 border-b border-line px-s5">
      {staff.group === null ? (
        <>
          <ProductMark />
          <span className="text-strong font-bold">{strings.staff.product}</span>
        </>
      ) : (
        <>
          <span aria-hidden="true" className="flex">
            <FaceMark face={staff.group} tile />
          </span>
          <span className="flex min-w-0 flex-col">
            <span role="img" aria-label={staff.group.name} className="min-w-0 truncate">
              <Wordmark face={staff.group} />
            </span>
            {staff.outlet != null && <span className="truncate text-caption font-semibold text-muted">{staff.outlet}</span>}
          </span>
        </>
      )}
    </div>
  );
}

/** The group's or brand's name as its wordmark: Baloo 2, the first word in the wordmark's first ink and the rest in its second (Kampong Mala). */
export function Wordmark({ face }: { readonly face: Face }) {
  const [first = '', ...rest] = face.name.split(/\s+/).filter((word) => word !== '');
  return (
    <span className="font-brand text-strong leading-none font-extrabold whitespace-nowrap">
      <span className={rest.length === 0 ? 'text-wordmark-2' : 'text-wordmark-1'}>{first}</span>
      {rest.length > 0 && <span className="text-wordmark-2"> {rest.join(' ')}</span>}
    </span>
  );
}

function BrandChip({ face }: { readonly face: Face }) {
  return (
    <span className="inline-flex h-control max-w-[16rem] min-w-0 items-center gap-s2 overflow-hidden rounded-chip border border-line bg-surface px-s4" aria-label={face.name} role="img">
      {face.logoUrl !== undefined ? (
        <FaceMark face={face} />
      ) : (
        <span className="min-w-0 truncate">
          <Wordmark face={face} />
        </span>
      )}
    </span>
  );
}

function AccountMenu({ who }: { readonly who: SignedIn }) {
  return (
    <UiMenu>
      <UiMenuTrigger asChild>
        <UiButton variant="ghost" size="compact" aria-label={`${strings.shell.account}: ${who.email}`}>
          <UiAvatar initials={who.email.slice(0, 2)} />
          <span className="hidden max-w-[14rem] truncate font-medium text-ink side:block">{who.email}</span>
          <ChevronDown aria-hidden="true" className="text-muted" />
        </UiButton>
      </UiMenuTrigger>
      <UiMenuContent>
        <UiMenuLabel>
          <span className="text-caption text-muted">{strings.shell.signedInAs}</span>
          <span className="truncate font-semibold">{who.email}</span>
        </UiMenuLabel>
        <UiMenuSeparator />
        <UiMenuItem
          onSelect={() => {
            void signOut().finally(() => {
              navigate('/sign-in', { replace: true });
            });
          }}
        >
          <LogOut aria-hidden="true" />
          {strings.staff.signOut}
        </UiMenuItem>
      </UiMenuContent>
    </UiMenu>
  );
}

/** The ask-or-search field, shown for where it will be, not yet working: an assistant needs a backend. */
function AskField() {
  return (
    <label className="relative hidden h-control w-full max-w-[30rem] min-w-0 items-center tablet:flex">
      <span className="sr-only">{strings.shell.askSoon}</span>
      <Sparkles aria-hidden="true" className="pointer-events-none absolute left-s3 size-icon text-muted" />
      <input disabled placeholder={strings.shell.ask} className="h-full w-full cursor-not-allowed rounded-control border border-line bg-sunk pr-[7.5rem] pl-s7 text-body text-ink placeholder:text-muted" />
      <span className="pointer-events-none absolute right-s2 flex items-center gap-s2">
        <kbd className="rounded-control border border-line bg-surface px-s2 py-s1 font-sans text-caption font-semibold text-muted">⌘K</kbd>
        <UiBadge tone="soon">{strings.shell.soon}</UiBadge>
      </span>
    </label>
  );
}

function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const update = () => {
      setOnline(navigator.onLine);
    };
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);
  return online;
}

function StatusBar() {
  const online = useOnline();
  return (
    <footer data-density="calm" aria-label={strings.shell.status} className="fixed inset-x-0 bottom-0 z-30 flex h-statusbar items-center gap-s4 bg-status-ground px-s4 font-sans text-caption text-on-status print:hidden">
      <span className="flex items-center gap-s2" role="status">
        <span className={cn('size-s2 rounded-chip', online ? 'bg-ok' : 'bg-late')} aria-hidden="true" />
        {online ? strings.shell.online : strings.shell.offline}
      </span>
      <span className="h-s3 w-px bg-status-muted opacity-50" aria-hidden="true" />
      <span className="flex items-center gap-s2 text-status-muted" aria-disabled="true">
        {strings.shell.autoCount}
        <span aria-hidden="true">·</span>
        {strings.shell.soon}
      </span>
    </footer>
  );
}

/** The phone's tab bar: the built modules, and More for the whole menu. */
function TabBar({ staff, onMore }: { readonly staff: StaffContext; readonly onMore: () => void }) {
  const built = useItems(staff).filter((item) => item.kind === 'built');
  const TAB = 'flex min-w-0 flex-1 cursor-pointer flex-col items-center justify-center gap-s1 text-caption font-semibold [&_svg]:size-icon';
  return (
    <nav data-density="calm" aria-label={strings.shell.menu} className="fixed inset-x-0 bottom-statusbar z-30 flex h-tabbar border-t border-line bg-surface px-s2 tablet:hidden print:hidden">
      {built.map((item) => {
        const Icon = ICONS[item.icon];
        return (
          <a
            key={item.href}
            href={item.href}
            aria-current={item.current ? 'page' : undefined}
            className={cn(TAB, 'text-muted aria-[current=page]:text-brand')}
            onClick={(event) => {
              follow(event, item.href, item.current, staff.onCurrent);
            }}
          >
            <Icon aria-hidden="true" />
            <span className="max-w-full truncate">{item.label}</span>
          </a>
        );
      })}
      <button type="button" className={cn(TAB, 'text-muted')} onClick={onMore}>
        <Ellipsis aria-hidden="true" />
        <span>{strings.shell.more}</span>
      </button>
    </nav>
  );
}

/** The frame around a staff page's content. StaffLayout holds it across the staff pages; a Screen given `staff` outside one draws its own. */
export function StaffFrame({ staff, kind = 'office', children }: { readonly staff: StaffContext; readonly kind?: ScreenKind; readonly children: ReactNode }) {
  useFrame(kind);
  const [drawer, setDrawer] = useState(false);
  const { path } = useAddress();
  // A page moved to closes the drawer, and takes the focus off the menu entry that was tapped (a page that moves it to its title keeps it there).
  useEffect(() => {
    setDrawer(false);
    const focused = document.activeElement;
    if (focused instanceof HTMLElement && focused.closest('main') === null) focused.blur();
  }, [path]);
  return (
    <div className="min-h-dvh bg-ground font-sans text-body text-ink antialiased side:grid side:grid-cols-[var(--spacing-sidebar)_minmax(0,1fr)]">
      <aside data-density="calm" className="sticky top-0 hidden h-dvh flex-col border-r border-line bg-surface pb-statusbar side:flex print:hidden">
        <SidebarHead staff={staff} />
        <NavList staff={staff} />
      </aside>
      <div className="flex min-h-dvh min-w-0 flex-col pb-[calc(var(--spacing-tabbar)+var(--spacing-statusbar))] tablet:pb-statusbar">
        <header data-density="calm" className="sticky top-0 z-20 flex h-topbar shrink-0 items-center gap-s3 border-b border-line bg-ground px-s4 tablet:px-s5 print:hidden">
          <span className="hidden tablet:flex side:hidden">
            <UiButton
              variant="ghost"
              size="icon"
              aria-label={strings.shell.openMenu}
              onClick={() => {
                setDrawer(true);
              }}
            >
              <Menu aria-hidden="true" />
            </UiButton>
          </span>
          <span className="flex items-center tablet:hidden">
            <ProductMark />
          </span>
          <AskField />
          <div className="ml-auto flex min-w-0 items-center gap-s2">
            {staff.group !== null && (
              <span className="flex min-w-0 side:hidden">
                <BrandChip face={staff.group} />
              </span>
            )}
            <LanguageMenu />
            {staff.who !== null && <AccountMenu who={staff.who} />}
          </div>
        </header>
        {children}
      </div>
      <TabBar
        staff={staff}
        onMore={() => {
          setDrawer(true);
        }}
      />
      <StatusBar />
      <UiSheet open={drawer} onOpenChange={setDrawer}>
        <UiSheetContent
          side="left"
          title={
            <>
              <ProductMark />
              <span className="text-strong font-bold">{strings.staff.product}</span>
            </>
          }
          closeLabel={strings.shell.close}
        >
          <NavList
            staff={staff}
            after={() => {
              setDrawer(false);
            }}
          />
        </UiSheetContent>
      </UiSheet>
    </div>
  );
}
